import React, { useState, useRef, useEffect } from 'react';
import { fetchPublicChatbotResponse } from '../api/openai';
import { db } from '../firebase';
import {
    collection,
    query,
    where,
    getDocs,
} from 'firebase/firestore';
import ReactMarkdown from 'react-markdown';

export default function ChatbotPublic() {
    const [messages, setMessages] = useState([
        {
            role: 'assistant',
            content: '你好，我係 TheraVan 小助手，有咩幫到你？',
            type: 'start'
        }
    ]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [isListening, setIsListening] = useState(false);
    const [bookingFlow, setBookingFlow] = useState(null);
    const [bookingData, setBookingData] = useState({});
    const chatEndRef = useRef(null);
    const recognitionRef = useRef(null);

    const scrollToBottom = () => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) {
            console.warn('瀏覽器唔支援語音輸入');
            return;
        }
        const recognition = new SpeechRecognition();
        recognition.lang = 'yue-Hant-HK';
        recognition.interimResults = false;
        recognition.continuous = false;

        recognition.onstart = () => setIsListening(true);
        recognition.onend = () => setIsListening(false);
        recognition.onerror = (e) => {
            console.error('Speech recognition error:', e);
            setIsListening(false);
        };
        recognition.onresult = (e) => {
            const transcript = e.results[0][0].transcript;
            setInput(prev => prev + transcript);
        };

        recognitionRef.current = recognition;
    }, []);

    const startListening = () => {
        recognitionRef.current?.start();
    };

    const speak = (text) => {
        if (!window.speechSynthesis) return;
        speechSynthesis.cancel();

        const doSpeak = () => {
            const utterance = new SpeechSynthesisUtterance(text);
            const voices = speechSynthesis.getVoices();
            const cantoneseVoice = voices.find(voice =>
                voice.lang.toLowerCase().includes('yue') ||
                voice.name.toLowerCase().includes('sin-ji') ||
                voice.name.toLowerCase().includes('tracy')
            );
            if (cantoneseVoice) {
                utterance.voice = cantoneseVoice;
            }
            utterance.lang = 'yue-HK';
            speechSynthesis.speak(utterance);
        };

        // 部分瀏覽器唔即時有 voices，要等 onvoiceschanged
        if (speechSynthesis.getVoices().length === 0) {
            speechSynthesis.onvoiceschanged = doSpeak;
        } else {
            doSpeak();
        }
    };


    const handleUserMessage = async (text) => {
        setMessages(prev => [...prev, { role: 'user', content: text }]);
        setInput('');
        setLoading(true);

        if (bookingFlow || /book|預約|時間/.test(text)) {
            handleBookingFlow(text);
        } else {
            try {
                const aiResponse = await fetchPublicChatbotResponse(text);
                setMessages(prev => [...prev, { role: 'assistant', content: aiResponse }]);
                speak(aiResponse);
            } catch (err) {
                const errMsg = '出錯了，請稍後再試。';
                setMessages(prev => [...prev, { role: 'assistant', content: errMsg }]);
                speak(errMsg);
            } finally {
                setLoading(false);
            }
        }
    };

    const handleBookingFlow = async (userInput) => {
        let flow = bookingFlow;
        let data = { ...bookingData };

        if (!flow) {
            flow = 'name';
            setBookingFlow(flow);
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: '請問你嘅姓名？'
            }]);
            setLoading(false);
            return;
        }

        if (flow === 'name') {
            data.name = userInput.trim();
            setBookingData(data);
            setBookingFlow('caseType');
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: '請問你主要邊度唔舒服？例如頸、腰、膝？'
            }]);
            setLoading(false);
            return;
        }

        if (flow === 'caseType') {
            data.caseType = userInput.trim();
            setBookingData(data);
            setBookingFlow('date');
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: '請問想預約邊日？請輸入 YYYY-MM-DD，例如 2025-01-31。'
            }]);
            setLoading(false);
            return;
        }

        if (flow === 'date') {
            data.date = userInput.trim();
            setBookingData(data);
            setBookingFlow('period');
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: '請問想揀上午定下午？',
                type: 'period-selection'
            }]);
            setLoading(false);
            return;
        }

        if (flow === 'period') {
            data.period = userInput.trim();
            setBookingData(data);

            const availableSlots = await suggestAvailableSlots(data.date, data.period);
            if (availableSlots.length === 0) {
                setMessages(prev => [...prev, {
                    role: 'assistant',
                    content: `呢個時段暫時冇空。想唔想換日或者換時間？`
                }]);
                setBookingFlow('date');
                setLoading(false);
                return;
            }

            setBookingData({
                ...data,
                suggestions: availableSlots
            });

            setBookingFlow('confirm');
            setMessages(prev => [...prev, {
                role: 'assistant',
                type: 'timeslot-selection',
                content: availableSlots
            }]);
            setLoading(false);
            return;
        }

        if (flow === 'confirm') {
            const chosenTime = userInput.trim();

            const therapist = await assignTherapist(data.date, chosenTime);
            if (!therapist) {
                setMessages(prev => [...prev, {
                    role: 'assistant',
                    content: `暫時全部治療師都滿額，請換日期或者換時間。`
                }]);
                setBookingFlow('date');
                setLoading(false);
                return;
            }

            const bookingUrl = `https://theravandemo.web.app/booking` +
                `?name=${encodeURIComponent(data.name)}` +
                `&preferredDate=${encodeURIComponent(data.date)}` +
                `&timeSlot=${encodeURIComponent(chosenTime)}` +
                `&bodyPart=${encodeURIComponent(data.caseType)}`;

            setMessages(prev => [...prev, {
                role: 'assistant',
                content: `✅ 已經幫你預留咗 **${chosenTime}**。\n\n請去呢度完成預約表單:\n[點此預約](${bookingUrl})`
            }]);

            speak(`已為你準備好預約連結！`);

            setBookingFlow(null);
            setBookingData({});
            setLoading(false);
        }
    };

    const suggestAvailableSlots = async (date, period) => {
        const slots = period === '上午'
            ? ['09:00', '10:00', '11:00']
            : ['14:00', '15:00', '16:00'];

        const available = [];

        for (const slot of slots) {
            const q = query(
                collection(db, 'sessions'),
                where('date', '==', date),
                where('timeSlot', '==', slot)
            );
            const snapshot = await getDocs(q);
            if (snapshot.size < 6) {
                available.push(slot);
            }
        }
        return available;
    };

    const assignTherapist = async (date, timeSlot) => {
        const rolesSnap = await getDocs(
            query(collection(db, 'roles'), where('role', '==', 'student'))
        );

        const students = rolesSnap.docs.map((doc) => ({
            therapistUID: doc.id,
            therapistName: doc.data().displayName || 'Unnamed',
        }));

        const available = [];

        for (const s of students) {
            const q = query(
                collection(db, 'sessions'),
                where('therapistUID', '==', s.therapistUID),
                where('date', '==', date),
                where('timeSlot', '==', timeSlot)
            );
            const snap = await getDocs(q);
            if (snap.empty) {
                available.push(s);
            }
        }

        if (available.length === 0) {
            return null;
        }

        return available[0];
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleUserMessage(input);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4 py-8">
            <div className="w-full max-w-xl bg-white shadow-lg rounded-lg flex flex-col h-[80vh]">
                <div className="p-4 border-b text-lg font-semibold text-center bg-blue-50">
                    💬 TheraVan Chatbot 小助手
                </div>
                <div className="flex-1 overflow-y-auto px-4 py-2 space-y-4">
                    {messages.map((msg, i) => (
                        <div
                            key={i}
                            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                            <div
                                className={`max-w-[80%] px-4 py-2 rounded-lg text-sm whitespace-pre-line shadow 
                                    ${msg.role === 'user'
                                        ? 'bg-blue-500 text-white rounded-br-none'
                                        : 'bg-gray-200 text-gray-800 rounded-bl-none'}`}
                            >
                                {msg.type === 'timeslot-selection' ? (
                                    <div className="flex flex-wrap gap-2 mt-2">
                                        {msg.content.map(slot => (
                                            <button
                                                key={slot}
                                                onClick={() => handleUserMessage(slot)}
                                                className="bg-blue-600 text-white px-3 py-2 rounded"
                                            >
                                                {slot}
                                            </button>
                                        ))}
                                    </div>
                                ) : msg.type === 'start' ? (
                                    <>
                                        <p>{msg.content}</p>
                                        <div className="flex gap-2 mt-2">
                                            <button
                                                onClick={() => handleUserMessage("我想預約")}
                                                className="bg-blue-500 text-white px-3 py-2 rounded"
                                            >
                                                📅 預約
                                            </button>
                                            <button
                                                onClick={() => handleUserMessage("我想查詢")}
                                                className="bg-gray-500 text-white px-3 py-2 rounded"
                                            >
                                                🔎 查詢
                                            </button>
                                        </div>
                                    </>
                                ) : msg.type === 'period-selection' ? (
                                    <>
                                        <p>{msg.content}</p>
                                        <div className="flex gap-2 mt-2">
                                            <button
                                                onClick={() => handleUserMessage("上午")}
                                                className="bg-blue-500 text-white px-3 py-2 rounded"
                                            >
                                                上午
                                            </button>
                                            <button
                                                onClick={() => handleUserMessage("下午")}
                                                className="bg-blue-500 text-white px-3 py-2 rounded"
                                            >
                                                下午
                                            </button>
                                        </div>
                                    </>
                                ) : (
                                    <ReactMarkdown
                                        components={{
                                            a: ({ node, ...props }) => (
                                                <a
                                                    {...props}
                                                    className="text-blue-600 underline"
                                                    target="_blank"
                                                    rel="noreferrer"
                                                />
                                            )
                                        }}
                                    >
                                        {msg.content}
                                    </ReactMarkdown>
                                )}
                            </div>
                        </div>
                    ))}
                    {loading && (
                        <div className="flex justify-start">
                            <div className="bg-gray-200 text-gray-700 text-sm px-4 py-2 rounded-lg animate-pulse">
                                正在回覆中...
                            </div>
                        </div>
                    )}
                    <div ref={chatEndRef} />
                </div>
                <div className="border-t p-3 flex items-center gap-2">
                    <button
                        onClick={startListening}
                        className={`px-3 py-2 rounded-full ${isListening ? 'bg-red-500' : 'bg-gray-200'} text-sm`}
                        title="語音輸入"
                    >
                        🎤
                    </button>
                    <input
                        type="text"
                        className="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                        placeholder="請輸入問題..."
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                    />
                    <button
                        onClick={() => handleUserMessage(input)}
                        disabled={loading}
                        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
                    >
                        傳送
                    </button>
                </div>
            </div>
        </div>
    );
}
